import React, { useState, useEffect, useRef } from "react";
import { MdThumbUp, MdThumbDown } from "react-icons/md";
import "../../Styles/AiFeedback.css";

// 14 rich confetti particles with compact spread parameter (~14-22px radius)
const CONFETTI_PARTICLES = [
  { tx: "-16px", ty: "-15px", rot: "-120deg", color: "#06b6d4", shape: "shape-ribbon", width: 3, height: 7, scale: 0.95 },
  { tx: "0px", ty: "-20px", rot: "45deg", color: "#ef4444", shape: "shape-circle", width: 4.5, height: 4.5, scale: 1 },
  { tx: "17px", ty: "-16px", rot: "135deg", color: "#f59e0b", shape: "shape-ribbon", width: 3, height: 8, scale: 0.85 },
  { tx: "21px", ty: "-4px", rot: "90deg", color: "#10b981", shape: "shape-pill", width: 3.5, height: 6, scale: 0.9 },
  { tx: "18px", ty: "13px", rot: "160deg", color: "#8b5cf6", shape: "shape-circle", width: 4.5, height: 4.5, scale: 0.8 },
  { tx: "7px", ty: "19px", rot: "210deg", color: "#ec4899", shape: "shape-ribbon", width: 3, height: 7, scale: 0.9 },
  { tx: "-9px", ty: "18px", rot: "-150deg", color: "#06b6d4", shape: "shape-pill", width: 3.5, height: 6, scale: 0.85 },
  { tx: "-19px", ty: "10px", rot: "-80deg", color: "#f97316", shape: "shape-circle", width: 4, height: 4, scale: 0.9 },
  { tx: "-22px", ty: "-3px", rot: "-45deg", color: "#a855f7", shape: "shape-ribbon", width: 3, height: 7, scale: 0.85 },
  { tx: "-10px", ty: "-12px", rot: "75deg", color: "#3b82f6", shape: "shape-pill", width: 3, height: 5.5, scale: 0.75 },
  { tx: "11px", ty: "-11px", rot: "-95deg", color: "#14b8a6", shape: "shape-circle", width: 4, height: 4, scale: 0.85 },
  { tx: "13px", ty: "7px", rot: "115deg", color: "#fbbf24", shape: "shape-ribbon", width: 3, height: 7, scale: 0.8 },
  { tx: "-5px", ty: "-17px", rot: "-140deg", color: "#ec4899", shape: "shape-pill", width: 3.5, height: 6, scale: 0.9 },
  { tx: "-14px", ty: "14px", rot: "130deg", color: "#22c55e", shape: "shape-circle", width: 4.5, height: 4.5, scale: 0.85 },
];

// Clean pure outline thumbs-down (no wrist box, no square boundary, just the hand outline in red stroke)
function PureThumbDownOutline({ size = 15, color = "#ef4444" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ overflow: "visible", display: "block" }}
    >
      <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
    </svg>
  );
}

export default function AiFeedbackActions({
  feedback,
  onFeedback,
  label = "Was this helpful?",
  size = "sm", // "sm" | "md"
  variant = "chat", // "chat" | "card"
  disabled = false,
  className = "",
}) {
  const [animatingLike, setAnimatingLike] = useState(false);
  const [animatingDislike, setAnimatingDislike] = useState(false);
  const [likeKey, setLikeKey] = useState(0);
  const [dislikeKey, setDislikeKey] = useState(0);

  const likeTimerRef = useRef(null);
  const dislikeTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (likeTimerRef.current) clearTimeout(likeTimerRef.current);
      if (dislikeTimerRef.current) clearTimeout(dislikeTimerRef.current);
    };
  }, []);

  const handleLike = (e) => {
    e.stopPropagation();
    if (disabled) return;

    setAnimatingLike(true);
    setLikeKey((prev) => prev + 1);

    if (likeTimerRef.current) clearTimeout(likeTimerRef.current);
    likeTimerRef.current = setTimeout(() => {
      setAnimatingLike(false);
    }, 850);

    onFeedback?.("like");
  };

  const handleDislike = (e) => {
    e.stopPropagation();
    if (disabled) return;

    setAnimatingDislike(true);
    setDislikeKey((prev) => prev + 1);

    if (dislikeTimerRef.current) clearTimeout(dislikeTimerRef.current);
    dislikeTimerRef.current = setTimeout(() => {
      setAnimatingDislike(false);
    }, 1500);

    onFeedback?.("dislike");
  };

  const iconSize = size === "md" ? 16 : 14;

  return (
    <div
      className={`ai-feedback-container variant-${variant} ${className}`}
      role="group"
      aria-label="AI response feedback"
    >
      {label && <span className="ai-feedback-label">{label}</span>}

      {/* LIKE BUTTON */}
      <div className="ai-feedback-btn-wrap">
        <button
          type="button"
          className={`ai-feedback-btn btn-like size-${size} ${
            feedback === "like" ? "is-active" : ""
          } ${animatingLike ? "is-animating-like" : ""}`}
          aria-label="Helpful (Like)"
          aria-pressed={feedback === "like"}
          disabled={disabled}
          onClick={handleLike}
        >
          <MdThumbUp size={iconSize} />
        </button>

        {/* Confetti Explosion on Like (Tighter radius spread) */}
        {animatingLike && (
          <div key={`like-confetti-${likeKey}`} className="ai-confetti-container" aria-hidden="true">
            {CONFETTI_PARTICLES.map((p, idx) => (
              <span
                key={idx}
                className={`ai-confetti-particle ${p.shape}`}
                style={{
                  "--tx": p.tx,
                  "--ty": p.ty,
                  "--rot": p.rot,
                  "--scale": p.scale,
                  width: `${p.width}px`,
                  height: `${p.height}px`,
                  backgroundColor: p.color,
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* DISLIKE BUTTON */}
      <div className="ai-feedback-btn-wrap">
        <button
          type="button"
          className={`ai-feedback-btn btn-dislike size-${size} ${
            feedback === "dislike" ? "is-active" : ""
          } ${animatingDislike ? "is-animating-dislike" : ""}`}
          aria-label="Not helpful (Dislike)"
          aria-pressed={feedback === "dislike"}
          disabled={disabled}
          onClick={handleDislike}
        >
          <MdThumbDown size={iconSize} />
        </button>

        {/* 3 Red Pure Outline Thumbsdown Cascading Downward with Gaps on Dislike */}
        {animatingDislike && (
          <div key={`dislike-trail-${dislikeKey}`} className="ai-dislike-trail-container" aria-hidden="true">
            <span className="ai-dislike-outline-item ai-dislike-outline-1">
              <PureThumbDownOutline size={iconSize + 1} />
            </span>
            <span className="ai-dislike-outline-item ai-dislike-outline-2">
              <PureThumbDownOutline size={iconSize} />
            </span>
            <span className="ai-dislike-outline-item ai-dislike-outline-3">
              <PureThumbDownOutline size={iconSize - 1} />
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
